import { useEffect, useRef, useState } from "react";
import { t } from "i18next";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { cn } from "@/lib/utils";
import useMapStore from "@/store/mapStore";
import { motion } from "framer-motion";
import LanguageToggle from "./LanguageToggle";
import { Button } from "./ui/button";
import { PiXBold } from "react-icons/pi";
import Bookmark from "@/assets/bookmark-solid.svg";
import Wifi from "@/assets/menu-wifi.svg";
import CustomerServiceDesk from "@/assets/menu-customer-service-desk.svg";
import Share from "@/assets/menu-rewards.svg";
import FindMyCar from "@/assets/menu-findmycar.svg";
import Offers from "@/assets/menu-offers.svg";
import Feedback from "@/assets/menu-feedback.svg";
import { FilterItemModel } from "@/models/filterItem.model";
import { filterCategories } from "@/store/data";
import useNavigateToAmenity from "@/hooks/useNavigateToAmenity";

function MoreAccordion() {
  const mobileMode = useMapStore((state) => state.mobileMode);
  const ads = useMapStore((state) => state.ads);
  const kioskMode = useMapStore((state) => state.kioskMode);
  const activeCategory = useMapStore((state) => state.activeCategory);
  const setOverlayContent = useMapStore((state) => state.setOverlayContent);
  const setActiveMenuItem = useMapStore((state) => state.setActiveMenuItem);
  const setActiveFilter = useMapStore((state) => state.setActiveFilter);

  const offersEnabled =
    import.meta.env.VITE_WAYFINDING_ENABLE_OFFERS === "true" && ads.length > 0;
  const findMyCarEnabled =
    import.meta.env.VITE_WAYFINDING_ENABLE_FIND_MY_CAR === "true";
  const feedbackEnabled =
    import.meta.env.VITE_WAYFINDING_COLLECT_FEEDBACK === "true";
  const showLanguageToggle = useRef<boolean>(
    import.meta.env.VITE_WAYFINDING_SHOW_LANGUAGE_TOGGLE === "true",
  );
  const [value, setValue] = useState<string>("item-1");
  const isOpen = value === "item-1";

  const [navigateToFacility] = useNavigateToAmenity();

  useEffect(() => {
    if (activeCategory?.id) {
      setValue(activeCategory.id);
    } else {
      setValue("item-1");
    }
  }, [activeCategory]);

  useEffect(() => {
    // Extract URL query parameters
    const urlParams = new URLSearchParams(window.location.search);

    // if language is defined in urlParams hide lagnuage toggle for mobile mode
    if (urlParams.get("language") && mobileMode) {
      showLanguageToggle.current = false;
    }
  }, [mobileMode]);

  const infoOpenHandler = (type: "wifi" | "share") => {
    if (type === "wifi") {
      const wifi = filterCategories[
        filterCategories.findIndex((i) => i.id === "facilities")
      ].items.find((i) => i.title === "wifi");
      if (wifi) {
        setActiveFilter(wifi as FilterItemModel);
      }
    }
    if (type === "share") {
      const share = filterCategories[
        filterCategories.findIndex((i) => i.id === "facilities")
      ].items.find((i) => i.title === "share");
      if (share) {
        setActiveFilter(share as FilterItemModel);
      }
    }
  };

  const serviceDeskOpenHandler = () => {
    navigateToFacility({ amenityTitle: "customer-service-desk" });
  };

  const accordionChangeHandler = (item: string) => {
    setValue(item);
  };

  if (mobileMode) {
    return (
      <motion.div
        key="menu"
        initial={{ opacity: 0, scale: 0 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0 }}
        transition={{ duration: 0.2 }}
        className={cn(
          "absolute top-[calc(100dvh-440px)] z-100 h-[440px] w-full rounded-t-2xl bg-white p-6 shadow-[0_3px_10px_rgb(0,0,0,0.2)] lg:top-[calc(100%-450px)]",
        )}
      >
        <h3 className="pb-4 text-xl font-bold">{t("more")}</h3>
        <Button
          size="icon"
          onClick={() => setActiveMenuItem("map")}
          className={cn(
            "absolute top-5 h-8 w-8 text-lg group-data-[dir=ltr]:right-5 group-data-[dir=rtl]:left-5",
          )}
        >
          <PiXBold />
        </Button>
        <ul className="space-y-2 pb-4 capitalize">
          {showLanguageToggle.current && (
            <li>
              <LanguageToggle />
            </li>
          )}
          <li
            className={cn(
              "flex min-h-[32px] cursor-pointer items-center gap-3 hover:text-primary",
            )}
            onClick={serviceDeskOpenHandler}
          >
            <span className="block w-[24px]">
              <img
                src={CustomerServiceDesk}
                alt={t("customer-service-desk")}
                className="w-[24px]"
              />
            </span>
            {t("customer-service-desk")}
          </li>
          <li
            className={cn(
              "flex min-h-[32px] cursor-pointer items-center gap-3 hover:text-primary",
            )}
            onClick={() => infoOpenHandler("wifi")}
          >
            <span className="block w-[24px]">
              <img src={Wifi} alt={t("wifi")} className="w-[24px]" />
            </span>
            {t("wifi")}
          </li>
          {offersEnabled && (
            <li
              className={cn(
                "flex min-h-[32px] cursor-pointer items-center gap-3 hover:text-primary",
              )}
              onClick={() => {
                setActiveMenuItem("map");
                setOverlayContent("offers");
              }}
            >
              <span className="block w-[24px]">
                <img src={Offers} alt={t("offers")} className="w-[24px]" />
              </span>
              {t("offers")}
            </li>
          )}
          <li
            className={cn(
              "flex min-h-[32px] cursor-pointer items-center gap-3 hover:text-primary",
            )}
            onClick={() => infoOpenHandler("share")}
          >
            <span className="block w-[24px]">
              <img src={Share} alt={t("share")} className="max-h-6 w-[24px]" />
            </span>
            {t("share")}
          </li>
          {feedbackEnabled && (
            <li
              className={cn(
                "flex min-h-[32px] cursor-pointer items-center gap-3 hover:text-primary",
              )}
              onClick={() => {
                setActiveMenuItem("map");
                setOverlayContent("feedback");
              }}
            >
              <span className="block w-[24px]">
                <img
                  src={Feedback}
                  alt={t("send-us-feedback")}
                  className="w-[24px]"
                />
              </span>
              {t("send-us-feedback")}
            </li>
          )}
        </ul>
      </motion.div>
    );
  }

  return (
    <>
      <Accordion
        type="single"
        collapsible
        value={value}
        onValueChange={accordionChangeHandler}
        className="w-full flex-1"
      >
        <AccordionItem
          value="item-1"
          className={cn("rounded-xl border-0 px-5")}
        >
          <AccordionTrigger
            className={cn(
              "max-w-full text-2xl font-bold outline-none hover:text-foreground hover:no-underline",
              isOpen && "hover:text-foreground",
            )}
          >
            <img
              src={Bookmark}
              alt=""
              className="w-[24px] px-1 group-data-[dir=ltr]:mr-3 group-data-[dir=rtl]:ml-3"
            />
            <span className="group-data-[dir=ltr]:mr-auto group-data-[dir=rtl]:ml-auto">
              {t("more")}
            </span>
          </AccordionTrigger>
          <AccordionContent>
            <ul className="mb-2 flex flex-col gap-3 capitalize">
              <li
                className={cn(
                  "group flex cursor-pointer items-center gap-3 rounded-md bg-gray-100 p-3 hover:bg-gray-200",
                )}
                onClick={serviceDeskOpenHandler}
              >
                <span className="block w-[24px]">
                  <img
                    src={CustomerServiceDesk}
                    alt={t("customer-service-desk")}
                    className="w-[24px]"
                  />
                </span>
                <p className="overflow-hidden text-ellipsis whitespace-nowrap text-base">
                  {t("customer-service-desk")}
                </p>
              </li>
              {findMyCarEnabled && (
                <li
                  className={cn(
                    "group flex cursor-pointer items-center gap-3 rounded-md bg-gray-100 p-3 hover:bg-gray-200",
                  )}
                  onClick={() => setOverlayContent("findMyCar")}
                >
                  <span className="block w-[24px]">
                    <img
                      src={FindMyCar}
                      alt={t("find-my-car")}
                      className="w-[24px]"
                    />
                  </span>
                  <p className="overflow-hidden text-ellipsis whitespace-nowrap text-base">
                    {t("find-my-car")}
                  </p>
                </li>
              )}
              {kioskMode && (
                <li
                  className={cn(
                    "group flex cursor-pointer items-center gap-3 rounded-md bg-gray-100 p-3 hover:bg-gray-200",
                  )}
                  onClick={() => infoOpenHandler("wifi")}
                >
                  <span className="block w-[24px]">
                    <img src={Wifi} alt={t("wifi")} className="w-[24px]" />
                  </span>
                  <p className="overflow-hidden text-ellipsis whitespace-nowrap text-base">
                    {t("wifi")}
                  </p>
                </li>
              )}
              {offersEnabled && (
                <li
                  className={cn(
                    "group flex cursor-pointer items-center gap-3 rounded-md bg-gray-100 p-3 hover:bg-gray-200",
                  )}
                  onClick={() => setOverlayContent("offers")}
                >
                  <span className="block w-[24px]">
                    <img src={Offers} alt={t("offers")} className="w-[24px]" />
                  </span>
                  <p className="overflow-hidden text-ellipsis whitespace-nowrap text-base">
                    {t("offers")}
                  </p>
                </li>
              )}
              {kioskMode && (
                <li
                  className={cn(
                    "group flex cursor-pointer items-center gap-3 rounded-md bg-gray-100 p-3 hover:bg-gray-200",
                  )}
                  onClick={() => infoOpenHandler("share")}
                >
                  <span className="block w-[24px]">
                    <img
                      src={Share}
                      alt={t("share")}
                      className="ml-[2px] max-h-6 w-[20px]"
                    />
                  </span>
                  <p className="overflow-hidden text-ellipsis whitespace-nowrap text-base">
                    {t("share")}
                  </p>
                </li>
              )}
              {kioskMode && feedbackEnabled && (
                <li
                  className={cn(
                    "group flex cursor-pointer items-center gap-3 rounded-md bg-gray-100 p-3 hover:bg-gray-200",
                  )}
                  onClick={() => setOverlayContent("feedback")}
                >
                  <span className="block w-[24px]">
                    <img
                      src={Feedback}
                      alt={t("send-us-feedback")}
                      className="w-[24px]"
                    />
                  </span>
                  <p className="overflow-hidden text-ellipsis whitespace-nowrap text-base">
                    {t("send-us-feedback")}
                  </p>
                </li>
              )}
            </ul>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </>
  );
}

export default MoreAccordion;
